import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Observable, Subject, combineLatest } from 'rxjs';
import { filter, finalize, map, takeUntil, tap } from 'rxjs/operators';

import { FieldError } from '../../api/models/field-error';
import { Invoice } from '../../api/models/invoice';
import { InvoiceStateId } from '../../api/models/invoice-state-id';
import { Money } from '../../api/models/money';
import { Permission } from '../../api/models/permission';
import { ReplaceInvoiceFinancesRequest } from '../../api/models/replace-invoice-finances-request';
import { InvoicesService } from '../../api/services/invoices.service';
import {
  ConfirmationDialogComponent,
  ConfirmationDialogModel,
} from '../../components/confirmation-dialog/confirmation-dialog.component';
import { FinancingMethodId } from '../../model/financing-method';
import {
  FinancingMethodsMap,
  FinancingMethodsMapCache,
  InvoiceConfigurationsMap,
  InvoiceConfigurationsMapCache,
  PenaltyInterestMethodsMap,
  PenaltyInterestMethodsMapCache,
} from '../../model/model-map-cache';
import { InvoicePeerService } from '../../service/invoice-peer.service';
import { UserService } from '../../service/user.service';
import { DateFields, clearFieldErrorOnEdit, toIsoDate } from '../../shared/date-fields/date-field.util';

/** The Finances fields that are date pickers. */
const FINANCES_DATE_FIELDS = ['financed_date', 'interest_end_date_override', 'final_payment_date'] as const;
type FinancesDateField = (typeof FINANCES_DATE_FIELDS)[number];

interface MapsBundle {
  configurations: InvoiceConfigurationsMap;
  financingMethods: FinancingMethodsMap;
  penaltyInterestMethods: PenaltyInterestMethodsMap;
}

// `details` payload of a `422 interest_rate_out_of_range` response. The
// thresholds are single-sourced in the backend; the widget renders its
// confirm message from these fields (see the backend's design.md D4).
interface InterestRateOutOfRangeDetails {
  financing_method_id: string;
  field: string;
  value: string;
  min: string;
  max: string;
  bound: string;
}

@Component({
  selector: 'app-invoice-financing-v2-widget',
  templateUrl: './invoice-financing-v2-widget.component.html',
  styleUrls: ['./invoice-financing-v2-widget.component.scss'],
})
export class InvoiceFinancingV2WidgetComponent implements OnInit, OnDestroy {
  private readonly invoicePeerService = inject(InvoicePeerService);
  private readonly invoicesService = inject(InvoicesService);
  private readonly builder = inject(UntypedFormBuilder);
  private readonly invoiceConfigurationsMapCache = inject(InvoiceConfigurationsMapCache);
  private readonly financingMethodsMapCache = inject(FinancingMethodsMapCache);
  private readonly penaltyInterestMethodsMapCache = inject(PenaltyInterestMethodsMapCache);
  private readonly dialog = inject(MatDialog);
  private readonly userService = inject(UserService);

  public readonly FinancingMethodId = FinancingMethodId;
  public readonly placeholder = '—';

  // States in which the server's per-state locked-fields guard
  // permits `final_payment_date` edits. The server is the source of
  // truth (`invoicestate.FinancesEditPolicyFor`); the widget mirrors
  // the gate so the editor isn't rendered when we know the server
  // would refuse it. Keep this list in sync with the policy.
  private static readonly FINAL_PAYMENT_DATE_LATE_STATES: ReadonlySet<InvoiceStateId> = new Set([
    InvoiceStateId.WaitingFinalPayment,
    InvoiceStateId.WaitingForReport,
    InvoiceStateId.Completed,
  ]);

  public invoice$: Observable<Invoice>;
  public maps$: Observable<MapsBundle>;
  // Gates the Edit button on the permission the backend enforces on
  // PUT /finances. Edit stays visible without it: the button is disabled,
  // with a tooltip saying why.
  public canEditFinances$: Observable<boolean>;
  // The Edit wrapper's tooltip; the template binds it only while the
  // permission is missing.
  public readonly editPermissionTooltip = "You don't have permission to edit invoice finances";

  public invoice: Invoice | null = null;
  public form: UntypedFormGroup;

  public edited = false;
  public locked = false;
  public saving = false;

  public stateLockedMessage: string | null = null;
  public reloadMessage: string | null = null;
  public fieldErrors: Record<string, string> = {};
  public financesInvalidSummary: string | null = null;
  public financesInvalidDetails: ReadonlyArray<{ field: string; message: string }> = [];

  // Financed Date, Interest End Date Override and Final Payment Date: each
  // picker's earliest day and the field's one message, as the Information
  // widget's date fields have. None has an upper limit.
  public readonly dateFields: DateFields<FinancesDateField>;

  private editEtag: string | null = null;
  private readonly ngDestroy$ = new Subject<void>();

  constructor() {
    this.form = this.builder.group({
      configuration_id: [null],
      financing_method_id: [null],
      daily_interest: [null],
      fixed_interest: [null],
      penalty_interest_method_id: [null],
      guarantee: [null],
      financed_date: [null],
      service_fee: [null],
      subfinancing_cost: [null],
      interest_end_date_override: [null],
      final_payment_date: [null],
    });
    this.dateFields = new DateFields<FinancesDateField>(this.form, (field) => this.invoice?.[field]);
  }

  public isFinalPaymentDateEditable(invoice: Invoice | null): boolean {
    return (
      invoice !== null
      && InvoiceFinancingV2WidgetComponent.FINAL_PAYMENT_DATE_LATE_STATES.has(invoice.state?.id)
    );
  }

  public ngOnInit(): void {
    this.maps$ = combineLatest([
      this.invoiceConfigurationsMapCache.map$,
      this.financingMethodsMapCache.map$,
      this.penaltyInterestMethodsMapCache.map$,
    ]).pipe(
      map(([configurations, financingMethods, penaltyInterestMethods]) => ({
        configurations,
        financingMethods,
        penaltyInterestMethods,
      })),
    );

    this.invoice$ = this.invoicePeerService.getInvoice().pipe(
      tap((invoice) => {
        this.invoice = invoice;
      }),
    );

    this.canEditFinances$ = this.userService.hasPermission(Permission.InvoiceEditFinances);

    this.invoicePeerService
      .getState()
      .pipe(takeUntil(this.ngDestroy$))
      .subscribe((state) => {
        this.locked = state === 'locked';
      });

    // The server's refusal of a date gives way to the picker's own check
    // once the handler edits that field, as in the Information widget. The
    // banner keeps the server's summary of the refused save until the next
    // Save, Edit or Cancel.
    clearFieldErrorOnEdit(this.form, FINANCES_DATE_FIELDS, this, this.ngDestroy$);
  }

  public ngOnDestroy(): void {
    this.ngDestroy$.next();
    this.ngDestroy$.complete();
  }

  public edit(): void {
    if (!this.invoice) {
      return;
    }
    this.invoicePeerService.lock();
    this.edited = true;
    this.editEtag = this.invoice.etag;
    this.clearErrors();
    this.form.reset({
      configuration_id: this.invoice.configuration_id ?? null,
      financing_method_id: this.invoice.financing_method_id ?? null,
      daily_interest: this.invoice.daily_interest ?? null,
      fixed_interest: this.invoice.fixed_interest ?? null,
      penalty_interest_method_id: this.invoice.penalty_interest_method_id ?? null,
      guarantee: this.invoice.guarantee ?? null,
      financed_date: this.invoice.financed_date ? new Date(this.invoice.financed_date) : null,
      service_fee: this.invoice.service_fee ? Number(this.invoice.service_fee.amount) : null,
      subfinancing_cost: this.invoice.subfinancing_cost
        ? Number(this.invoice.subfinancing_cost.amount)
        : null,
      interest_end_date_override: this.invoice.interest_end_date_override
        ? new Date(this.invoice.interest_end_date_override)
        : null,
      final_payment_date: this.invoice.final_payment_date
        ? new Date(this.invoice.final_payment_date)
        : null,
    });
    this.form.markAsPristine();
  }

  public revert(): void {
    this.invoicePeerService.unlock();
    this.edited = false;
    this.editEtag = null;
    this.form.reset();
    this.clearErrors();
  }

  public clearInterestEndDateOverride(): void {
    this.form.patchValue({ interest_end_date_override: null });
    this.form.get('interest_end_date_override')?.markAsDirty();
  }

  public clearFinancedDate(): void {
    this.form.patchValue({ financed_date: null });
    this.form.get('financed_date')?.markAsDirty();
  }

  public clearFinalPaymentDate(): void {
    this.form.patchValue({ final_payment_date: null });
    this.form.get('final_payment_date')?.markAsDirty();
  }

  public save(): void {
    if (!this.invoice || !this.editEtag) {
      return;
    }

    // Pre-validate: required non-nullable fields must have a value
    // either on the form or on the loaded invoice. If both are empty,
    // we'd otherwise silently send a placeholder ('0', '', { amount:
    // '0' }) that the schema accepts but produces semantically wrong
    // data. Surface inline instead.
    this.clearErrors();
    const missing = this.requiredFieldsMissing(this.invoice);
    if (missing.length > 0) {
      const details = missing.map((field) => ({ field, message: 'required' }));
      this.financesInvalidDetails = details;
      this.financesInvalidSummary = `Cannot save: required field${missing.length > 1 ? 's' : ''} missing on this invoice: ${missing.join(', ')}.`;
      const inline: Record<string, string> = {};
      for (const e of details) {
        inline[e.field] = e.message;
      }
      this.fieldErrors = inline;
      return;
    }

    const body = this.buildPutBody(this.invoice);
    this.submit(body);
  }

  // submit performs the actual PUT for the built body, carrying the
  // captured `If-Match` etag. save() and the interest-rate override
  // retry both funnel through here so success/error handling lives in
  // one place. The etag is cleared only on success, so a failed first
  // attempt (e.g. interest_rate_out_of_range) can be retried with the
  // still-valid etag.
  private submit(body: ReplaceInvoiceFinancesRequest): void {
    if (!this.invoice || !this.editEtag) {
      return;
    }

    this.saving = true;

    this.invoicesService
      .replaceInvoiceFinances({
        id: this.invoice.id,
        'If-Match': this.editEtag,
        body,
      })
      // The HttpErrorInterceptor answers a 403 with its own dialog and ends
      // the request with neither a value nor an error, so Save is re-enabled
      // however the request ends. The form stays in edit mode; Cancel leaves.
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: (updated) => {
          this.edited = false;
          this.editEtag = null;
          this.invoicePeerService.unlock();
          this.invoicePeerService.invoiceChanged(updated);
        },
        error: (err: HttpErrorResponse) => {
          if (
            err.status === 422
            && err.error?.error_code === 'interest_rate_out_of_range'
          ) {
            this.showInterestRateOverrideDialog(err.error.details, body);
            return;
          }
          this.handleError(err);
        },
      });
  }

  // showInterestRateOverrideDialog opens a confirm dialog explaining the
  // out-of-band interest value. On confirm it resubmits the same body
  // with `allow_interest_rate_override: true` (and the still-valid etag);
  // on cancel nothing is saved and the form stays editable.
  private showInterestRateOverrideDialog(
    details: InterestRateOutOfRangeDetails,
    body: ReplaceInvoiceFinancesRequest,
  ): void {
    const dialogData = new ConfirmationDialogModel(
      'Interest rate out of range',
      this.buildInterestRateMessage(details),
      'Save anyway',
      'Cancel',
    );
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      maxWidth: '400px',
      data: dialogData,
    });
    dialogRef
      .afterClosed()
      .pipe(filter((confirmed) => !!confirmed))
      .subscribe(() => {
        this.submit({ ...body, allow_interest_rate_override: true });
      });
  }

  private buildInterestRateMessage(details: InterestRateOutOfRangeDetails): string {
    const label = details.field === 'fixed_interest' ? 'Fixed interest' : 'Daily interest';
    if (details.bound === 'above') {
      return `${label} ${details.value} % exceeds the allowed maximum ${details.max} %. Save anyway?`;
    }
    return `${label} ${details.value} % is below the allowed minimum ${details.min} %. Save anyway?`;
  }

  public reload(): void {
    if (!this.invoice) {
      return;
    }
    this.reloadMessage = null;
    this.invoicePeerService.initialize(this.invoice.id);
  }

  public hasFieldError(field: string): boolean {
    return field in this.fieldErrors;
  }

  public getFieldError(field: string): string | null {
    return this.fieldErrors[field] ?? null;
  }

  // Required-on-the-wire (non-nullable on the OpenAPI request schema).
  // Pre-validated by save() so buildPutBody can resolve them via the
  // form / loaded-invoice chain without a sentinel fallback. Update
  // this list and `requiredFieldsMissing` together when the schema
  // changes.
  private requiredFieldsMissing(invoice: Invoice): string[] {
    const c = this.form.controls;
    const missing: string[] = [];
    if (!this.hasValue(c['configuration_id'].value, invoice.configuration_id)) {
      missing.push('configuration_id');
    }
    if (!this.hasValue(c['financing_method_id'].value, invoice.financing_method_id)) {
      missing.push('financing_method_id');
    }
    if (!this.hasValue(c['guarantee'].value, invoice.guarantee)) {
      missing.push('guarantee');
    }
    if (!this.hasValue(c['subfinancing_cost'].value, invoice.subfinancing_cost)) {
      missing.push('subfinancing_cost');
    }
    return missing;
  }

  private hasValue(formValue: unknown, invoiceValue: unknown): boolean {
    if (formValue !== null && formValue !== undefined && formValue !== '') {
      return true;
    }
    return invoiceValue !== null && invoiceValue !== undefined && invoiceValue !== '';
  }

  private buildPutBody(invoice: Invoice): ReplaceInvoiceFinancesRequest {
    const c = this.form.controls;
    return {
      configuration_id: this.numberOrFallback(c['configuration_id'].value, invoice.configuration_id),
      financing_method_id: this.stringOrFallback(c['financing_method_id'].value, invoice.financing_method_id),
      daily_interest: this.toDecimalStringOrNull(c['daily_interest'].value),
      fixed_interest: this.toDecimalStringOrNull(c['fixed_interest'].value),
      penalty_interest_method_id: this.stringOrNull(c['penalty_interest_method_id'].value),
      guarantee: this.toDecimalStringOrNull(c['guarantee'].value)
        ?? this.toDecimalStringOrNull(invoice.guarantee)
        ?? this.throwMissing('guarantee'),
      // An emptied date picker, a cleared one included, sends an explicit
      // null; any other day goes out as ISO YYYY-MM-DD.
      financed_date: toIsoDate(c['financed_date'].value),
      service_fee: this.toMoneyOrNull(c['service_fee'].value),
      subfinancing_cost: this.toMoneyOrFallback(c['subfinancing_cost'].value, invoice.subfinancing_cost),
      interest_end_date_override: toIsoDate(c['interest_end_date_override'].value),
      final_payment_date: toIsoDate(c['final_payment_date'].value),
    };
  }

  // Invariant: pre-validation in save() guarantees one of (form,
  // loaded-invoice) carries a value for every required field, so this
  // function should be unreachable. If it fires, requiredFieldsMissing
  // is missing a field or the call sites diverged. Throw loudly.
  private throwMissing(field: string): never {
    throw new Error(
      `v2 widget invariant violated: required field "${field}" is empty on both form and loaded invoice after pre-validation`,
    );
  }

  private numberOrFallback(value: unknown, fallback: number | undefined): number {
    if (value === null || value === undefined || value === '') {
      if (fallback === null || fallback === undefined) {
        return this.throwMissing('configuration_id');
      }
      return fallback;
    }
    return Number(value);
  }

  private stringOrFallback(value: unknown, fallback: string | undefined): string {
    if (value === null || value === undefined || value === '') {
      if (fallback === null || fallback === undefined || fallback === '') {
        return this.throwMissing('financing_method_id');
      }
      return fallback;
    }
    return String(value);
  }

  private stringOrNull(value: unknown): string | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    return String(value);
  }

  private toDecimalStringOrNull(value: unknown): string | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    return String(value);
  }

  private toMoneyOrNull(value: unknown): Money | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    return { amount: String(value), currency: 'EUR' };
  }

  private toMoneyOrFallback(value: unknown, fallback: Money | undefined): Money {
    const money = this.toMoneyOrNull(value);
    if (money !== null) {
      return money;
    }
    if (fallback) {
      return fallback;
    }
    return this.throwMissing('subfinancing_cost');
  }

  private handleError(err: HttpErrorResponse): void {
    if (err.status === 412) {
      this.reloadMessage =
        'Invoice was updated by another user. Reload to see the latest.';
      return;
    }
    if (err.status === 422 && err.error?.error_code === 'invoice_state_locked') {
      const stateName = err.error?.details?.state ?? 'current state';
      this.stateLockedMessage = `Editing is not allowed in state "${stateName}".`;
      return;
    }
    if (
      err.status === 422
      && (err.error?.error_code === 'invoice_finances_invalid'
        || err.error?.error_code === 'invoice_field_locked')
    ) {
      const rawErrors: ReadonlyArray<FieldError> = err.error?.errors ?? [];
      const details = rawErrors.map((e) => ({ field: e.field, message: e.message }));
      const inline: Record<string, string> = {};
      for (const e of details) {
        inline[e.field] = e.message;
      }
      this.fieldErrors = inline;
      this.financesInvalidDetails = details;
      this.financesInvalidSummary =
        err.error?.message ?? 'Invoice finances are invalid.';
    }
  }

  private clearErrors(): void {
    this.stateLockedMessage = null;
    this.reloadMessage = null;
    this.fieldErrors = {};
    this.financesInvalidSummary = null;
    this.financesInvalidDetails = [];
  }
}
